import { CSS_PREFIX, READY_TIMEOUT_MS } from "./constants";
import { buildIframeSrc, parseIncomingMessage } from "./protocol";
import { buildModal, lockBodyScroll, trapFocus, unlockBodyScroll, type ModalElements } from "./dom";
import type { WidgetConfig, WidgetConfigInput } from "./types";

const LABELS = {
  close: "Sluiten",
  loading: "Reserveren laden…",
  errorTitle: "Reserveren kan momenteel niet worden geladen. Probeer het opnieuw.",
  retry: "Opnieuw proberen",
};

/**
 * Section 27 — this class is the "host/widget shell": overlay, modal,
 * open/close, responsive behavior, loading/error states, iframe
 * lifecycle. It knows nothing about tickets/availability/reservations —
 * that's entirely the iframe app's concern, reached only through the
 * versioned postMessage protocol in protocol.ts.
 */
export class BrothersReservationsWidget {
  private config: WidgetConfig;
  private elements: ModalElements | null = null;
  private open_ = false;
  private iframeLoaded = false;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFocusedElement: HTMLElement | null = null;
  private dirty = false;
  /**
   * Notified after ANY close, including the ones that never go through the
   * public api.close(): the X button, Escape, and a CLOSE_REQUEST from the
   * iframe. index.ts uses this to reveal the launcher again — without it, a
   * guest who closes with the X would lose the only way back in.
   */
  private closedListeners: (() => void)[] = [];
  private readonly onKeyDown = (event: KeyboardEvent) => this.handleKeyDown(event);
  private readonly onMessage = (event: MessageEvent) => this.handleMessage(event);

  constructor(config: WidgetConfig) {
    this.config = config;
  }

  configure(next: WidgetConfigInput): void {
    this.config = { ...this.config, ...next };
  }

  isOpen(): boolean {
    return this.open_;
  }

  onClosed(listener: () => void): void {
    this.closedListeners.push(listener);
  }

  /** Section 21: nothing (not even the modal DOM/iframe) is created until the first real open() call. */
  open(): void {
    if (this.open_) return;
    this.open_ = true;

    if (!this.elements) {
      this.elements = buildModal(LABELS);
      this.elements.closeButton.addEventListener("click", () => this.close());
      this.elements.retryButton.addEventListener("click", () => this.retryIframe());
      // Section 10: the backdrop itself (a click that lands on the overlay
      // but NOT inside the modal) deliberately does not close — only the
      // explicit close button, Escape, and CLOSE_REQUEST do, so a guest
      // can never lose a half-filled reservation from a stray click.
    }

    this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    lockBodyScroll();
    document.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("message", this.onMessage);

    this.elements.overlay.hidden = false;
    // Force a layout flush so the opacity transition actually runs instead
    // of the hidden->visible and opacity change coalescing into one frame.
    void this.elements.overlay.offsetHeight;
    this.elements.overlay.classList.add(`${CSS_PREFIX}-overlay--open`);

    if (!this.iframeLoaded) {
      this.startLoadingIframe();
    }

    this.elements.modal.focus({ preventScroll: true });
  }

  close(): void {
    if (!this.open_) return;
    this.open_ = false;

    if (this.elements) {
      this.elements.overlay.classList.remove(`${CSS_PREFIX}-overlay--open`);
      this.elements.overlay.hidden = true;
    }

    document.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("message", this.onMessage);
    unlockBodyScroll();

    // Section 22: reset the guest flow on close so the next open() starts
    // clean, rather than silently holding a stale reservation attempt.
    this.teardownIframe();

    // preventScroll: unlockBodyScroll() above already restored the exact
    // pre-open scroll position (section 9) — a plain .focus() call
    // defaults to scrolling its target into view and would silently
    // undo that restore.
    this.lastFocusedElement?.focus({ preventScroll: true });
    this.lastFocusedElement = null;

    for (const listener of this.closedListeners) listener();
  }

  /** Test/debug hook — not part of the public API surface. */
  destroy(): void {
    this.close();
    this.elements?.overlay.remove();
    this.elements = null;
  }

  private startLoadingIframe(): void {
    if (!this.elements) return;
    this.elements.loadingStatus.hidden = false;
    this.elements.errorStatus.hidden = true;
    this.elements.iframe.hidden = true;
    this.elements.iframe.src = buildIframeSrc(this.config.bookingUrl);
    this.iframeLoaded = true;

    this.readyTimer = setTimeout(() => this.showError(), READY_TIMEOUT_MS);
    this.elements.iframe.addEventListener("error", () => this.showError(), { once: true });
  }

  private retryIframe(): void {
    this.iframeLoaded = false;
    this.startLoadingIframe();
  }

  private teardownIframe(): void {
    if (this.readyTimer) {
      clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
    if (this.elements) {
      this.elements.iframe.src = "about:blank";
      this.elements.iframe.hidden = true;
      this.elements.loadingStatus.hidden = false;
      this.elements.errorStatus.hidden = true;
    }
    this.iframeLoaded = false;
    this.dirty = false;
  }

  private showReady(): void {
    if (this.readyTimer) {
      clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
    if (!this.elements) return;
    this.elements.loadingStatus.hidden = true;
    this.elements.errorStatus.hidden = true;
    this.elements.iframe.hidden = false;
  }

  private showError(): void {
    if (!this.elements) return;
    this.elements.loadingStatus.hidden = true;
    this.elements.iframe.hidden = true;
    this.elements.errorStatus.hidden = false;
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.close();
      return;
    }
    if (this.elements) trapFocus(this.elements.modal, event);
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.elements) return;
    const expectedOrigin = new URL(this.config.bookingUrl).origin;
    const message = parseIncomingMessage(event, this.elements.iframe.contentWindow, expectedOrigin);
    if (!message) return;

    switch (message.type) {
      case "READY":
        this.showReady();
        return;
      case "RESERVATION_COMPLETED":
        // Section 23: the reservation is already committed server-side —
        // this event is purely informational (analytics/integration
        // hooks). The modal stays open so the guest can read the
        // confirmation screen; it is never closed automatically here.
        return;
      case "CLOSE_REQUEST":
        this.close();
        return;
      case "FLOW_STATE_CHANGED":
        // Section 11: tracked for a future close-confirmation UX: no
        // confirmation dialog is built in this pass (documented, not
        // implemented, per the explicit "no complex new system now").
        this.dirty = message.dirty;
        return;
    }
  }
}
